import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ENABLE_FULL_RESPONSIVE } from 'reactApp/appHelpers/configHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import type { TextInputModalProps } from 'reactApp/ui/TextInputModal/TextInputModal.types';

import styles from './TextInputModal.css';
import { TextInputModalContent } from './TextInputModalContent';

export const TextInputModal = memo(function TextInputModal(props: TextInputModalProps): ReactElement {
    const { onClose, onActionClick, id } = props;

    const isPhone = useSelector(EnvironmentSelectors.isPhone);
    const ref = useRef<HTMLDivElement>(null);

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleAccept = useCallback(
        (value: string) => {
            onActionClick?.(value);
            onClose?.();
        },
        [onActionClick, onClose]
    );

    if (isPhone) {
        return (
            <MobileDialog id={id} mod="confirm" topmost onClose={handleClose} scrollRef={ref}>
                <div className={styles.fullContent} ref={ref}>
                    <TextInputModalContent {...props} handleAccept={handleAccept} handleClose={handleClose} isPhone={isPhone} />
                </div>
            </MobileDialog>
        );
    }

    return (
        <Dialog
            open
            size="small"
            onCancel={handleClose}
            id={props.id}
            className={classNames(styles.root, {
                [styles.rootResponsive]: ENABLE_FULL_RESPONSIVE,
            })}
        >
            <Content isModal isLoading={false} hasError={false} scrolling>
                <TextInputModalContent {...props} handleAccept={handleAccept} handleClose={handleClose} isPhone={isPhone} />
            </Content>
        </Dialog>
    );
});
